import { useState, useEffect } from 'react';
import { useAuth } from '../../../app/providers';
import { useNavigate } from '@tanstack/react-router';
import { onboardingApi, type Organization } from '../api';
import { organizationApi } from '../../settings/api/organizationApi';
import toast from 'react-hot-toast';

const FinalizePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'success' | 'error'
  >('idle');
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Fetch organization data on mount
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const org = await onboardingApi.getOrganization();
        setOrganization(org);
        // Set preview URL if organization has a logo
        if (org?.logo) {
          setPreviewUrl(org.logo);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load organization');
      }
    };

    fetchOrganization();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPreviewUrl(URL.createObjectURL(file));
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploadStatus('uploading');

      // Create a unique but consistent filename to overwrite previous uploads
      // preserving the original extension
      const fileExtension = file.name.split('.').pop() || 'png';
      const fileName = `organization-profile.${fileExtension}`;

      const { signedUrl, path } = await onboardingApi.getUploadUrl(
        fileName,
        file.type,
      );

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update organization with the new logo
      await organizationApi.updateSettings({ logo: path });
      
      setUploadStatus('success');
      toast.success('Profile picture uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      toast.error('Failed to upload profile picture');
    }
  };

  const handleGetStarted = async () => {
    if (!acceptedTerms) {
      setError('Please accept the terms and conditions');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onboardingApi.completeOnboarding();
      await refreshUser(); // Update user state with isOnboardingComplete = true
      navigate({ to: '/' });
    } catch (err: any) {
      setError(err.message || 'Failed to complete setup');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-0 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-4 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Welcome {user?.name}!
          </h1>
          <p className="text-lg md:text-xl text-gray-600">
            Start managing your sober living community with ease.
          </p>
        </div>

        {/* Getting Finalize Section */}
        <div className="mb-4 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
            Getting Started
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Finalize Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-8">
            {/* Profile Picture Upload */}
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-[#5761EB]/10 rounded-full flex items-center justify-center mx-auto mb-4 relative overflow-hidden">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Organization Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl text-[#5761EB]">🏢</span>
                )}
                {uploadStatus === 'uploading' && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <label
                htmlFor="onboarding-profile-selection"
                className="text-[#5761EB] hover:text-[#5761EB]/80 text-sm font-medium cursor-pointer"
              >
                {uploadStatus === 'uploading'
                  ? 'Uploading...'
                  : 'Upload profile picture'}
              </label>
              <input
                type="file"
                className="hidden"
                id="onboarding-profile-selection"
                onChange={handleFileSelect}
                accept="image/*"
                disabled={uploadStatus === 'uploading'}
              />
            </div>

            {/* Organization Info */}
            {organization ? (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {organization.name}
                </h3>
                <p className="text-gray-600 mb-4">{organization.address}</p>

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Organization number
                  </span>
                  <span className="text-sm text-gray-600">
                    {organization.phoneNumber}
                  </span>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <p className="text-gray-500 text-center">
                  Loading organization details...
                </p>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="mb-6">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-[#5761EB] focus:ring-[#5761EB] border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  I've accept all the{' '}
                  <a
                    href="#"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Terms & conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="#"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Privacy policy
                  </a>
                  .
                </span>
              </label>
            </div>

            {/* Get Started Button */}
            <div className="text-center">
              <button
                onClick={handleGetStarted}
                disabled={isLoading || !acceptedTerms}
                className="bg-[#5761EB] text-white py-3 px-8 rounded-lg font-medium hover:bg-[#5761EB]/90 focus:outline-none focus:ring-2 focus:ring-[#5761EB] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Setting up...' : 'Get started'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinalizePage;
